<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Calendar as CalendarIcon } from 'lucide-vue-next'
import { type DateValue, parseDate } from '@internationalized/date'
import {
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
} from '@/components/shadcn'
import { cn } from '@/lib/utils'

// Props 정의
const props = withDefaults(
  defineProps<{
    /** v-model로 양방향 데이터 바인딩 (ISO 8601 문자열: YYYY-MM-DD) */
    modelValue?: string | null
    /** Input 요소의 id */
    id?: string
    /** 입력 전 표시되는 안내문 */
    placeholder?: string
    /** 비활성화 상태 */
    disabled?: boolean
    /** 필수 입력 여부 */
    required?: boolean
    /** form 데이터 전송 시 키 이름 */
    name?: string
    /** 추가 CSS 클래스 */
    class?: string
    /** 스타일 테마 지정 */
    styletype?: 'default' | 'sm' | 'lg'
  }>(),
  {
    placeholder: '날짜를 선택하세요',
    styletype: 'default',
  }
)

// 이벤트 정의
const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  'change': [value: string | null]
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

// 스타일 프리셋
const STYLE_PRESETS: Record<string, { buttonClass: string }> = {
  default: {
    buttonClass: 'h-7 text-xs px-2.5',
  },
  sm: {
    buttonClass: 'h-7 text-[11px] px-2',
  },
  lg: {
    buttonClass: 'h-9 text-sm px-3',
  },
}

// 내부 상태로 DateValue 관리
const internalDate = ref<DateValue | undefined>()

// modelValue를 DateValue로 변환
const convertToDateValue = (value: string | null | undefined): DateValue | undefined => {
  if (!value) return undefined
  
  try {
    // ISO 8601 문자열을 직접 parseDate로 변환
    return parseDate(value)
  } catch {
    return undefined
  }
}

// DateValue를 ISO 문자열로 변환
const convertToString = (date: DateValue | undefined): string | null => {
  if (!date) return null
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
}

// modelValue 변경 감지
watch(
  () => props.modelValue,
  (newValue) => {
    const converted = convertToDateValue(newValue)
    if (JSON.stringify(internalDate.value) !== JSON.stringify(converted)) {
      internalDate.value = converted
    }
  },
  { immediate: true }
)

// 내부 날짜 변경 시 emit
const handleDateChange = (date: DateValue | undefined) => {
  internalDate.value = date
  const stringValue = convertToString(date)
  
  if (stringValue !== props.modelValue) {
    emit('update:modelValue', stringValue)
    emit('change', stringValue)
  }
}

// 포맷팅된 날짜 표시
const formattedDate = computed(() => {
  if (!internalDate.value) return props.placeholder
  
  const date = new Date(
    internalDate.value.year,
    internalDate.value.month - 1,
    internalDate.value.day
  )
  
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
})

// 버튼 클래스 계산
const buttonClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  const userClass = props.class || ''
  
  return cn(
    'w-full justify-start text-left font-normal',
    preset?.buttonClass ?? 'h-10',
    !internalDate.value && 'text-muted-foreground',
    userClass
  )
})
</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button
        :id="id"
        :name="name"
        :disabled="disabled"
        :class="buttonClass"
        variant="outline"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      >
        <CalendarIcon class="mr-2 h-4 w-4" />
        {{ formattedDate }}
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-auto p-0">
      <Calendar
        v-model="internalDate"
        :locale="'ko-KR'"
        initial-focus
        @update:model-value="handleDateChange"
      />
    </PopoverContent>
  </Popover>
</template>
